import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, CheckCircle, XCircle, Filter, FileText, MapPin,
  Calendar, Loader2, AlertCircle, ChevronLeft, ChevronRight, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/Table';
import { useUltimaVarredura, useDecidirVarreduraItem } from '@/hooks/useFunilVarredura';
import { FunilItemDetalhesModal } from '@/components/funil/FunilItemDetalhesModal';
import type { FunilVarreduraItem } from '@/types/empresa';

type TabType = 'recomendados' | 'nao-recomendados';

function formatCurrency(value: number | null | undefined) {
  if (value === null || value === undefined || value === 0) return '-';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0
  }).format(value);
}

function formatDate(value: string | null | undefined) {
  if (!value) return '-';
  return new Date(value).toLocaleDateString('pt-BR');
}

function ScoreBar({ score }: { score: number }) {
  let color = 'bg-ds-danger';
  if (score >= 75) color = 'bg-ds-success';
  else if (score >= 50) color = 'bg-ds-warning';
  else if (score >= 25) color = 'bg-ds-info';

  return (
    <div className="flex items-center gap-2">
      <div className="w-20 h-2 rounded-full bg-ds-surface2 overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${score}%` }} />
      </div>
      <span className="text-xs font-medium text-ds-text-primary">{score}</span>
    </div>
  );
}

function EmptyState({ message, submessage }: { message: string; submessage?: string }) {
  return (
    <div className="text-center py-10">
      <Filter className="h-12 w-12 text-ds-text-secondary mx-auto mb-3" />
      <p className="text-ds-text-secondary">{message}</p>
      {submessage && <p className="text-sm text-ds-text-secondary mt-1">{submessage}</p>}
    </div>
  );
}

function Pagination({
  page,
  totalPages,
  onChange
}: {
  page: number;
  totalPages: number;
  onChange: (page: number) => void;
}) {
  if (totalPages <= 1) return null;

  return (
    <div className="flex items-center justify-between pt-4 border-t border-ds-border">
      <p className="text-sm text-ds-text-secondary">
        Página {page} de {totalPages}
      </p>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onChange(Math.max(1, page - 1))}
          disabled={page <= 1}
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Anterior
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onChange(Math.min(totalPages, page + 1))}
          disabled={page >= totalPages}
        >
          Próxima
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}

function ItemRow({
  item,
  empresaId,
  showMotivo,
  onSelect
}: {
  item: FunilVarreduraItem;
  empresaId: string;
  showMotivo: boolean;
  onSelect: (item: FunilVarreduraItem) => void;
}) {
  const decidir = useDecidirVarreduraItem(empresaId);

  const handleDecisao = (decisao: 'aceito' | 'descartado') => {
    decidir.mutate({ itemId: item.id, decisao });
  };

  const isDecidido = item.decisaoRevisor !== 'pendente';

  return (
    <TableRow>
      <TableCell>
        <button
          type="button"
          onClick={() => onSelect(item)}
          className="max-w-xs text-left w-full"
        >
          <p className="text-sm font-medium text-ds-text-primary truncate hover:underline">
            {item.licitacao.titulo || 'Sem título'}
          </p>
          <div className="flex flex-wrap items-center gap-2 mt-1">
            {item.licitacao.orgao && (
              <span className="text-xs text-ds-text-secondary">{item.licitacao.orgao}</span>
            )}
            {item.licitacao.uf && (
              <Badge variant="info" size="sm">
                <MapPin className="h-3 w-3 mr-0.5" />
                {item.licitacao.uf}
              </Badge>
            )}
            {item.licitacao.valorEstimado !== null && item.licitacao.valorEstimado !== undefined && (
              <span className="text-xs text-ds-success">{formatCurrency(item.licitacao.valorEstimado)}</span>
            )}
          </div>
        </button>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2 text-xs text-ds-text-secondary">
          <Calendar className="h-3.5 w-3.5" />
          {formatDate(item.licitacao.prazoAbertura)}
        </div>
      </TableCell>
      <TableCell>
        <ScoreBar score={item.score} />
      </TableCell>
      {showMotivo && (
        <TableCell>
          <p className="text-xs text-ds-text-secondary max-w-[220px] truncate" title={item.motivo}>
            {item.motivo || '-'}
          </p>
        </TableCell>
      )}
      <TableCell className="text-right">
        <div className="flex items-center justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => handleDecisao('aceito')}
            disabled={isDecidido && item.decisaoRevisor === 'aceito'}
            isLoading={decidir.isPending}
            className="text-ds-success hover:text-ds-success hover:bg-ds-success/10"
            title="Aceitar envia para a Gestão de Licitações"
          >
            <CheckCircle className="h-4 w-4 mr-1" />
            Aceitar
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => handleDecisao('descartado')}
            disabled={isDecidido && item.decisaoRevisor === 'descartado'}
            isLoading={decidir.isPending}
            className="text-ds-danger hover:text-ds-danger hover:bg-ds-danger/10"
          >
            <XCircle className="h-4 w-4 mr-1" />
            Descartar
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

export function FunilVarredura() {
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState<TabType>('recomendados');
  const [page, setPage] = useState(1);
  const [selectedItem, setSelectedItem] = useState<FunilVarreduraItem | null>(null);

  const recomendado = activeTab === 'recomendados';
  const { data, isLoading, error, refetch } = useUltimaVarredura(id, {
    recomendado,
    page,
    limit: 20
  });
  const decidirModal = useDecidirVarreduraItem(id);

  const handleModalDecisao = (decisao: 'aceito' | 'descartado') => {
    if (!selectedItem) return;
    decidirModal.mutate(
      { itemId: selectedItem.id, decisao },
      { onSuccess: () => setSelectedItem(null) }
    );
  };

  if (!id) {
    return (
      <div className="rounded-lg border border-ds-danger/30 bg-ds-danger/10 p-8 text-center text-ds-danger">
        <AlertCircle className="h-10 w-10 mx-auto mb-3" />
        <h2 className="text-lg font-semibold">Empresa não informada</h2>
      </div>
    );
  }

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    setPage(1);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Link
            to={`/empresas/${id}/perfil-tecnico`}
            className="inline-flex items-center justify-center rounded-lg border border-ds-border bg-ds-surface p-2 text-ds-text-secondary hover:text-ds-text-primary hover:bg-ds-surface2 transition-colors"
            aria-label="Voltar para perfil da empresa"
          >
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-ds-text-primary">Funil de Licitações</h1>
            <p className="text-ds-text-secondary mt-1">
              Resultado da varredura ativa de oportunidades compatíveis
            </p>
          </div>
        </div>
        <Button
          type="button"
          variant="secondary"
          onClick={() => refetch()}
          isLoading={isLoading}
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Atualizar
        </Button>
      </div>

      {isLoading && !data && (
        <div className="flex items-center justify-center h-64" role="status">
          <Loader2 className="h-10 w-10 animate-spin text-ds-success" />
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-ds-danger/30 bg-ds-danger/10 p-6 text-center text-ds-danger">
          <AlertCircle className="h-10 w-10 mx-auto mb-3" />
          <h2 className="text-lg font-semibold">Erro ao carregar varredura</h2>
          <p className="mt-1">Não foi possível carregar o resultado da varredura.</p>
        </div>
      )}

      {!isLoading && !error && !data && (
        <Card>
          <CardContent className="p-8">
            <EmptyState
              message="Nenhuma varredura encontrada para esta empresa."
              submessage="Volte ao perfil da empresa e clique em 'Ativar Funil' para executar uma varredura."
            />
          </CardContent>
        </Card>
      )}

      {data?.varredura && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-ds-text-secondary">Analisados</p>
                    <p className="text-2xl font-bold text-ds-text-primary mt-1">
                      {data.varredura.totalEditaisAnalisados}
                    </p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-ds-surface2 flex items-center justify-center">
                    <FileText className="h-5 w-5 text-ds-info" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-ds-text-secondary">Recomendados</p>
                    <p className="text-2xl font-bold text-ds-text-primary mt-1">
                      {data.varredura.totalRecomendados}
                    </p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-ds-success/10 flex items-center justify-center">
                    <CheckCircle className="h-5 w-5 text-ds-success" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-ds-text-secondary">Não recomendados</p>
                    <p className="text-2xl font-bold text-ds-text-primary mt-1">
                      {data.varredura.totalEditaisAnalisados - data.varredura.totalRecomendados}
                    </p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-ds-danger/10 flex items-center justify-center">
                    <XCircle className="h-5 w-5 text-ds-danger" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-ds-text-secondary">Executada em</p>
                    <p className="text-lg font-bold text-ds-text-primary mt-1">
                      {formatDate(data.varredura.concluidoEm || data.varredura.iniciadoEm)}
                    </p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-ds-warning/10 flex items-center justify-center">
                    <Calendar className="h-5 w-5 text-ds-warning" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <CardTitle>Oportunidades encontradas</CardTitle>
                  <CardDescription>
                    {recomendado
                      ? 'Editais que atingiram o score mínimo configurado no funil'
                      : 'Editais filtrados pelo funil e o motivo da não recomendação'}
                  </CardDescription>
                </div>
                <div className="flex border border-ds-border rounded-lg overflow-hidden">
                  <button
                    type="button"
                    onClick={() => handleTabChange('recomendados')}
                    className={`px-4 py-2 text-sm font-medium transition-colors ${
                      activeTab === 'recomendados'
                        ? 'bg-ds-success text-white'
                        : 'bg-ds-surface text-ds-text-secondary hover:text-ds-text-primary'
                    }`}
                  >
                    Recomendados
                  </button>
                  <button
                    type="button"
                    onClick={() => handleTabChange('nao-recomendados')}
                    className={`px-4 py-2 text-sm font-medium transition-colors border-l border-ds-border ${
                      activeTab === 'nao-recomendados'
                        ? 'bg-ds-danger text-white'
                        : 'bg-ds-surface text-ds-text-secondary hover:text-ds-text-primary'
                    }`}
                  >
                    Não recomendados
                  </button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {data.itens.length === 0 ? (
                <EmptyState
                  message={recomendado ? 'Nenhuma oportunidade recomendada desta vez.' : 'Nenhum edital foi filtrado fora do funil.'}
                  submessage={recomendado ? 'Tente ajustar as palavras-chave, UFs ou reduzir o score mínimo.' : undefined}
                />
              ) : (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Edital</TableHead>
                        <TableHead>Prazo</TableHead>
                        <TableHead>Score</TableHead>
                        {!recomendado && <TableHead>Motivo</TableHead>}
                        <TableHead className="text-right">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.itens.map((item) => (
                        <ItemRow
                          key={item.id}
                          item={item}
                          empresaId={id}
                          showMotivo={!recomendado}
                          onSelect={setSelectedItem}
                        />
                      ))}
                    </TableBody>
                  </Table>
                  <Pagination
                    page={data.pagination.page}
                    totalPages={data.pagination.totalPages}
                    onChange={setPage}
                  />
                </>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {selectedItem && (
        <FunilItemDetalhesModal
          item={selectedItem}
          scoreMinimo={data?.varredura?.scoreMinimo ?? null}
          onClose={() => setSelectedItem(null)}
          onAceitar={() => handleModalDecisao('aceito')}
          onDescartar={() => handleModalDecisao('descartado')}
          isProcessing={decidirModal.isPending}
        />
      )}
    </div>
  );
}

export default FunilVarredura;
